import * as vp from 'vopay';
import { useState, FC } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFileDownload from '@/hooks/useFiledDownload';
import BankDetailsCard from './Card/BankDetailsCard';
import RecipientDetailsCard from './Card/RecipientDetailsCard';
import styles from '../Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

import {
  Table,
  Box,
  SubTitle,
  Button,
  DropDown,
  formattedCurrency,
  Description,
  BaseTransactionResponse,
  AutoBalanceTransferReportResponse,
  TransactionsLists,
  withStyles,
  Card,
} from 'vopay';

import { TransactionCard, Title, DetailsRows } from 'UI';

const cn = withStyles(styles);

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  accountId: string;
};

type TransactionInfoProps = {
  transaction: BaseTransactionResponse;
  autoBalanceTransferReport: AutoBalanceTransferReportResponse;
  transactionHeader: TransactionsLists;
  jwt: string;
};

type TablePropsHandlers = {
  autoBalanceReport: () => vp.TableProps;
};

const AutoBalanceTransferTransactionInfo: FC<TransactionInfoProps> = ({
  transaction,
  transactionHeader,
  autoBalanceTransferReport,
  jwt,
}: TransactionInfoProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.AutoBalanceTransferTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const {
    response: { JWT: mainJwt },
  } = useClientData();
  const isMainAccount = !jwt || jwt === mainJwt;

  const { state } = useLocation<DataProps>();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();

  const recipientName =
    transaction.FirstName && transaction.LastName
      ? `${transaction.FirstName} ${transaction.LastName}`
      : transaction.CompanyName
        ? transaction.CompanyName
        : '-';

  const transactionDetails = [
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Global.TransactionTypes.Auto Balance Transfer', {
        defaultValue: 'Auto Balance Transfer',
      }),
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.holdReleaseDate', { defaultValue: 'Hold Release Date' }),
      value: transaction.HoldReleaseDateTime,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transactionHeader.Notes || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.FailureReason || '-',
    },
  ];

  const address = {
    Address1: transaction.Address1,
    Address2: transaction.Address2,
    City: transaction.City,
    Province: transaction.Province || '',
    Country: transaction.Country,
    PostalCode: transaction.PostalCode || '',
  };

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={recipientName}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={'Auto Balance Transfer'}
        failureReason={transaction.FailureReason}
        contactId={state?.accountId}
        jwt={jwt}
        isMainAccount={isMainAccount}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <RecipientDetailsCard
        receiverName={recipientName}
        accountID={state?.accountId}
        address={address}
        isMainAccount={isMainAccount}
      />
      <BankDetailsCard
        details={{
          AccountNumber: transaction.AccountNumber,
          FinancialInstitutionNumber: transaction.FinancialInstitutionNumber,
          BranchTransitNumber: transaction.BranchTransitNumber,
        }}
      />

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}

      <Box mt="lg" mb="md" jc="sb">
        <SubTitle subTitle={t('autoBalanceTransactionBreakdown') || ''} />
        {autoBalanceTransferReport.Success && (
          <div className={styles.dropdownContainer}>
            <Button
              id="export-transactions"
              color="none"
              label={t('ExportDd.export', { defaultValue: 'Export' })}
              height="38px"
              textSize={1}
              background="transparent"
              noShadow
              icon="arrowDown"
              iconLocation="right"
              onClick={() => {
                setIsMenuVisible(true);
              }}
            />
            {isMenuVisible && (
              <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                <Button
                  id="export-PDF-btn"
                  label={t('ExportDd.pdf', { defaultValue: 'PDF' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadPdfTable('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-CSV-btn"
                  label={t('ExportDd.csv', { defaultValue: 'CSV' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadCsvFile('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-XLS-btn"
                  label={t('ExportDd.xls', { defaultValue: 'XLS' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadXls('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-DOC-btn"
                  label={t('ExportDd.doc', { defaultValue: 'DOC' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadDoc('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-Text-btn"
                  label={t('ExportDd.text', { defaultValue: 'TEXT' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadTxt('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-JSON-btn"
                  label={t('ExportDd.json', { defaultValue: 'JSON' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadJson(
                      Object.values(autoBalanceTransferReport.Transactions).map((reportTransaction) => ({
                        id: reportTransaction.TransactionID,
                        name: reportTransaction.FullName,
                        transactionDate: reportTransaction.TransactionDate,
                        type: tGlobal(`Global.TransactionTypes.${reportTransaction.TransactionType}`, {
                          defaultValue: reportTransaction.TransactionType,
                        }),
                        referenceNumber: reportTransaction.ReferenceNo,
                        amount: `$${
                          reportTransaction.CreditAmount === '0.00'
                            ? formattedCurrency(reportTransaction.DebitAmount)
                            : formattedCurrency(reportTransaction.CreditAmount)
                        }`,
                      })),
                      'auto-balance-transfer-transactions',
                    );
                  }}
                />
                <Button
                  id="export-XML-btn"
                  label={t('ExportDd.xml', { defaultValue: 'XML' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadXml('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-Print-btn"
                  label={t('ExportDd.print', { defaultValue: 'Print' })}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    printFile('auto-balance-transactions-table');
                  }}
                />
              </DropDown>
            )}
          </div>
        )}
      </Box>
      {autoBalanceTransferReport.Success && <Table {...getTableProps('autoBalanceReport')} />}
      {!autoBalanceTransferReport.Success && (
        <Description>
          {t('transactionsNotRetrieved', {
            defaultValue: 'The transactions for this auto-balance transfer could not be retrieved.',
          })}
        </Description>
      )}
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      autoBalanceReport: () => ({
        id: 'auto-balance-transactions-table',
        name: 'transactions',
        sortable: true,
        columns: {
          id: { header: t('autoBalanceReport.id', { defaultValue: 'ID' }) || '', align: 'left' },
          name: { header: t('autoBalanceReport.name', { defaultValue: 'Name' }) || '', align: 'left' },
          transactionDate: {
            header: t('autoBalanceReport.transactionDate', { defaultValue: 'Transaction Date' }) || '',
            align: 'left',
          },
          type: { header: t('autoBalanceReport.type', { defaultValue: 'Transaction Type' }) || '', align: 'left' },
          referenceNumber: {
            header: t('autoBalanceReport.referenceNumber', { defaultValue: 'Reference Number' }) || '',
            align: 'left',
          },
          creditAmount: {
            header: t('autoBalanceReport.creditAmount', { defaultValue: 'Credit Amount' }) || '',
            align: 'left',
          },
          debitAmount: {
            header: t('autoBalanceReport.debitAmount', { defaultValue: 'Debit Amount' }) || '',
            align: 'left',
          },
          details: { header: '', align: 'right', isSortable: false },
        },
        rows: Object.values(autoBalanceTransferReport.Transactions).map((reportTransaction, index) => ({
          id: reportTransaction.TransactionID,
          name: reportTransaction.FullName,
          transactionDate: reportTransaction.TransactionDate,
          type: tGlobal(`Global.TransactionTypes.${reportTransaction.TransactionType}`, {
            defaultValue: reportTransaction.TransactionType,
          }),
          referenceNumber: reportTransaction.ReferenceNo,
          creditAmount: `$${formattedCurrency(reportTransaction.CreditAmount)}`,
          debitAmount: `$${formattedCurrency(reportTransaction.DebitAmount)}`,
          details: (
            <Link
              to={{
                pathname: `/transactions-history/details/${vp.removeWhiteSpaceURI(reportTransaction.TransactionType)}/${
                  reportTransaction.TransactionID
                }/${transactionHeader.Currency}`,
                state: {
                  transactionId: reportTransaction.TransactionID,
                },
              }}
            >
              <Button
                id={`transaction-details-${index}`}
                label={t('autoBalanceReport.details', { defaultValue: 'Details' })}
                color="lined"
                noShadow
                height="30px"
                fontUnit="px"
                textSize={14}
              />
            </Link>
          ),
        })),
      }),
    };
    return handlers[handler]();
  }
};

export default AutoBalanceTransferTransactionInfo;
